// components/ui/password-input.tsx
import * as React from "react";
import { cn } from "@/lib/utils";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "./input";
import { Control, Controller } from "react-hook-form";

interface PasswordInputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "onChange" | "value" | "name"
  > {
  control: Control<any>; // RHF control
  name: string; // Field name
  label?: string;
  errorMessage?: string;
  successMessage?: string;
  warningMessage?: string;
  showStrengthMeter?: boolean;
}

const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  (
    {
      className,
      label,
      errorMessage,
      successMessage,
      warningMessage,
      showStrengthMeter = true,
      control,
      name,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = React.useState(false);

    return (
      <Controller
        control={control}
        name={name}
        render={({ field }) => {
          const value = typeof field.value === "string" ? field.value : "";

          // Calculate password strength
          const passwordStrength = React.useMemo(() => {
            if (!value) return "weak";

            const hasUppercase = /[A-Z]/.test(value);
            const hasLowercase = /[a-z]/.test(value);
            const hasNumber = /\d/.test(value);
            const hasSymbol = /[!@#$%^&*(),.?":{}|<>]/.test(value);
            const length = value.length;

            let score = 0;
            if (hasUppercase) score += 1;
            if (hasLowercase) score += 1;
            if (hasNumber) score += 1;
            if (hasSymbol) score += 1;
            if (length >= 8) score += 1;

            if (score <= 2) return "weak";
            if (score === 3) return "average";
            return "strong";
          }, [value]);

          return (
            <div className="space-y-1">
              {label && (
                <label
                  htmlFor={props.id || name}
                  className="block text-sm font-medium leading-6 text-gray-700"
                >
                  {label}
                </label>
              )}

              <div className="relative">
                <Input
                  id={props.id || name}
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  className={cn("pr-10", className)}
                  {...props}
                  {...field}
                  value={value}
                />

                <button
                  type="button"
                  className="absolute inset-y-0 right-3 flex items-center justify-center rounded-r-md p-1.5 hover:bg-gray-200"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-500" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-500" />
                  )}
                </button>
              </div>

              {showStrengthMeter && (
                <>
                  <div className="mt-2 flex items-center space-x-2">
                    <span
                      className={cn(
                        "h-1 w-10 rounded-full bg-gray-200",
                        passwordStrength === "weak" && "bg-red-500",
                        passwordStrength === "average" && "bg-yellow-500",
                        passwordStrength === "strong" && "bg-green-500"
                      )}
                    ></span>
                    <span
                      className={cn(
                        "h-1 w-10 rounded-full bg-gray-200",
                        passwordStrength === "average" && "bg-yellow-500",
                        passwordStrength === "strong" && "bg-green-500"
                      )}
                    ></span>
                    <span
                      className={cn(
                        "h-1 w-10 rounded-full bg-gray-200",
                        passwordStrength === "strong" && "bg-green-500"
                      )}
                    ></span>
                  </div>

                  {value && (
                    <p
                      className={cn(
                        "mt-1 text-sm",
                        passwordStrength === "weak" && "text-red-500",
                        passwordStrength === "average" && "text-yellow-500",
                        passwordStrength === "strong" && "text-green-500"
                      )}
                    >
                      {passwordStrength.charAt(0).toUpperCase() +
                        passwordStrength.slice(1)}{" "}
                      password
                    </p>
                  )}

                  {!value && (
                    <ul className="mt-1 list-disc pl-5 text-sm text-gray-500">
                      <li>Upper & lower case letters</li>
                      <li>A symbol ($@#)</li>
                      <li>A long password</li>
                    </ul>
                  )}
                </>
              )}

              {errorMessage && (
                <p className="mt-1 text-sm text-red-500">{errorMessage}</p>
              )}
              {successMessage && (
                <p className="mt-1 text-sm text-green-500">{successMessage}</p>
              )}
              {warningMessage && (
                <p className="mt-1 text-sm text-yellow-500">{warningMessage}</p>
              )}
            </div>
          );
        }}
      />
    );
  }
);

PasswordInput.displayName = "PasswordInput";
export default PasswordInput;
